import { Container, Graphics, Sprite, TilingSprite } from 'pixi.js';
import { bakeFrames } from '../art/bake';
import { COAT_BAY } from '../art/palettes';
import { HORSE_IDLE } from '../art/sprites/horse';
import {
  BARN,
  CLOUD_BIG,
  CLOUD_SMALL,
  FENCE,
  GRASS_TILE,
  TREE_BIG,
  TREE_SMALL,
  type Showpiece,
} from '../art/sprites/scenery';
import { bindKeys } from '../input/InputController';
import { VIRTUAL_H, VIRTUAL_W } from '../render/StageScaler';
import { clearSave, hasSave, loadGame } from '../sim/ranch/save';
import { RanchSim } from '../sim/ranch/sim';
import { el } from '../ui/dom';
import { IS_DESKTOP } from '../variant';
import { GrayboxScene } from './GrayboxScene';
import { RanchScene } from './RanchScene';
import type { Scene, SceneContext } from './SceneManager';

export interface TitleOptions {
  canPersist: boolean;
  forceNew: boolean;
  seed: number | null;
}

const ITCH_URL = 'https://kentsgames.itch.io';
const KENTS_GAMES_URL = 'https://kentsgames.com';

function linkButton(label: string, href: string): HTMLElement {
  return el('a', { class: 'button link', href, target: '_blank', rel: 'noopener' }, label);
}

export class TitleScene implements Scene {
  private readonly options: TitleOptions;
  private ctx!: SceneContext;
  private panel!: HTMLElement;
  private backdrop: Container | null = null;
  private unbind: (() => void) | null = null;

  constructor(options: TitleOptions) {
    this.options = options;
  }

  enter(ctx: SceneContext): void {
    this.ctx = ctx;
    this.backdrop = new Container();
    const sky = new Graphics()
      .rect(0, 0, VIRTUAL_W, 40)
      .fill(0x8fb0d0)
      .rect(0, 40, VIRTUAL_W, 60)
      .fill(0x87a8c8)
      .rect(0, 100, VIRTUAL_W, 12)
      .fill(0x6f94b8);
    this.backdrop.addChild(sky);
    const pasture = new TilingSprite({
      texture: bakeFrames(GRASS_TILE.def, GRASS_TILE.palette)[0]!,
      width: VIRTUAL_W,
      height: VIRTUAL_H - 112,
    });
    pasture.position.set(0, 112);
    this.backdrop.addChild(pasture);
    const put = (piece: Showpiece, x: number, groundY: number): void => {
      const sprite = new Sprite(bakeFrames(piece.def, piece.palette)[0]!);
      sprite.position.set(x - piece.def.anchor[0], groundY - piece.def.h);
      this.backdrop!.addChild(sprite);
    };
    put(CLOUD_BIG, 100, 30);
    put(CLOUD_SMALL, 360, 22);
    put(TREE_BIG, 440, 130);
    put(TREE_SMALL, 20, 126);
    put(BARN, 70, 190);
    for (let fx = 130; fx < 480; fx += 24) put(FENCE, fx, 196);
    const horse = new Sprite(bakeFrames(HORSE_IDLE, COAT_BAY)[0]!);
    horse.position.set(360 - HORSE_IDLE.anchor[0], 250 - HORSE_IDLE.h);
    this.backdrop.addChild(horse);
    ctx.scaler.root.addChild(this.backdrop);

    const saveExists = this.options.canPersist && !this.options.forceNew && hasSave();

    const buttons: HTMLElement[] = [];
    if (saveExists) {
      buttons.push(
        el('button', { class: 'button primary', onclick: () => this.continueGame() }, 'Continue (Enter)'),
      );
    }
    buttons.push(
      el(
        'button',
        { class: 'button' + (saveExists ? '' : ' primary'), onclick: () => this.newGame() },
        saveExists ? 'New game — erases save' : 'New game (Enter)',
      ),
      el(
        'button',
        { class: 'button', onclick: () => this.ctx.scenes.push(new GrayboxScene(this.options.seed)) },
        'Race lab',
      ),
    );
    if (!IS_DESKTOP) buttons.push(linkButton('Download for free on Itch.io!', ITCH_URL));
    buttons.push(linkButton("View more of Kent's Games", KENTS_GAMES_URL));

    this.panel = el(
      'div',
      { class: 'panel graybox-panel interactive title-panel' },
      el('div', { class: 'graybox-title' }, 'DERBY DAYS'),
      el('div', { class: 'graybox-sub' }, 'raise them · train them · ride them'),
      ...buttons,
    );
    ctx.uiRoot.append(this.panel);

    this.unbind = bindKeys([
      {
        code: 'Enter',
        handler: () => {
          if (saveExists) this.continueGame();
          else this.newGame();
        },
      },
    ]);
  }

  frame(): void {
    // Static.
  }

  suspend(): void {
    this.panel.style.display = 'none';
    if (this.backdrop) this.backdrop.visible = false;
  }

  resume(): void {
    this.panel.style.display = '';
    if (this.backdrop) this.backdrop.visible = true;
  }

  private continueGame(): void {
    const loaded = loadGame();
    if (!loaded) {
      this.newGame();
      return;
    }
    this.ctx.scenes.replace(new RanchScene(loaded, this.options.canPersist));
  }

  private newGame(): void {
    clearSave();
    const seed = this.options.seed ?? Date.now() >>> 0;
    this.ctx.scenes.replace(new RanchScene(RanchSim.newGame(seed), this.options.canPersist));
  }

  exit(): void {
    this.unbind?.();
    this.panel.remove();
    this.backdrop?.destroy({ children: true });
    this.backdrop = null;
  }
}
